/**
 * Página de manutenção do site.
 * O mesmo HTML vale no palco, na visualização e no Moodle.
 * O aviso "Avise-me" não entra: não há envio de e-mail neste elemento.
 */

export const MAINTENANCE_LIGHT = {
  bgColor: '#f8f9ff',
  textColor: '#0b1c30',
  mutedColor: '#464555',
  cardColor: '#ffffff',
  cardText: '#0b1c30',
  accentColor: '#4f46e5',
  accent2Color: '#0891b2',
  okColor: '#059669',
  trackColor: '#e5eeff',
};

export const MAINTENANCE_DARK = {
  bgColor: '#0b1326',
  textColor: '#dae2fd',
  mutedColor: '#c7c4d7',
  cardColor: '#131b2e',
  cardText: '#dae2fd',
  accentColor: '#8083ff',
  accent2Color: '#4cd7f6',
  okColor: '#4edea3',
  trackColor: '#2d3449',
};

function escText(value) {
  return String(value ?? '').replace(/[&<>]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]));
}

function escAttr(value) {
  return escText(value).replace(/"/g, '&quot;');
}

function safeColor(value, fallback) {
  const v = String(value || '').trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)) return v;
  return fallback;
}

function safeUrl(value) {
  const url = String(value || '').trim();
  if (!url || /^(javascript|data):/i.test(url)) return '';
  return url;
}

function clamp(value, min, max, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function toLocalInput(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function defaultSchedule() {
  const start = new Date();
  start.setSeconds(0, 0);
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  return { startsAt: toLocalInput(start), endsAt: toLocalInput(end) };
}

/** Instantâneo do campo datetime-local, no fuso de quem configurou. */
export function maintenanceInstantFromInput(value) {
  const raw = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) return 0;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function formatSchedule(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return '';
  return `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}`;
}

function clockFromWindow(startMs, endMs, nowMs) {
  const start = Number(startMs) || 0;
  const end = Number(endMs) || 0;
  const now = Number(nowMs) || Date.now();
  let percent = 0;
  let left = 0;
  if (end > start && start > 0) {
    percent = Math.round(Math.max(0, Math.min(100, ((now - start) / (end - start)) * 100)));
  } else if (end > 0 && now >= end) {
    percent = 100;
  }
  if (end > now) left = Math.floor((end - now) / 1000);
  return {
    percent,
    hours: Math.floor(left / 3600),
    minutes: Math.floor((left % 3600) / 60),
    seconds: left % 60,
  };
}

function safeId(widgetId) {
  return String(widgetId || 'mnt').replace(/[^a-zA-Z0-9_-]/g, '') || 'mnt';
}

function hexToRgba(hex, alpha) {
  const raw = String(hex || '').replace('#', '').trim();
  const full = raw.length === 3 ? raw.split('').map((ch) => ch + ch).join('') : raw;
  const n = parseInt(full, 16);
  if (!Number.isFinite(n) || full.length !== 6) return `rgba(79,70,229,${alpha})`;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

export function createDefaultMaintenanceContent(text = {}) {
  return {
    theme: 'light',
    ...MAINTENANCE_LIGHT,
    bgImage: '',
    publishMaintenance: 'keep',
    badge: text.badge || 'Manutenção programada',
    version: text.version || 'v2.4',
    title: text.title || 'Estamos aprimorando sua experiência',
    subtitle: text.subtitle || 'A plataforma está recebendo melhorias de estabilidade e desempenho. Voltamos em instantes.',
    showProgress: true,
    progressLabel: text.progressLabel || 'Progresso da atualização',
    progressHint: text.progressHint || 'Otimização e migração',
    ...defaultSchedule(),
    forecastLabel: text.forecastLabel || 'Previsão',
    showCountdown: true,
    hoursLabel: text.hoursLabel || 'Horas',
    minutesLabel: text.minutesLabel || 'Minutos',
    secondsLabel: text.secondsLabel || 'Segundos',
    showDiagnostics: true,
    diagnosticsTitle: text.diagnosticsTitle || 'Diagnóstico ao vivo',
    diagnostics: [
      { name: text.diag1 || 'Servidores', status: text.diag1status || 'Operacional', tone: 'ok' },
      { name: text.diag2 || 'Banco e cache', status: text.diag2status || 'Migrando', tone: 'busy' },
      { name: text.diag3 || 'Interface', status: text.diag3status || '98%', tone: 'info' },
    ],
    latency: text.latency || 'Latência 18 ms',
    cluster: text.cluster || 'Cluster local',
    showGame: true,
    gameTitle: text.gameTitle || 'Salto do Saber',
    gameBadge: text.gameBadge || 'Modo relax',
    gameText: text.gameText || 'O ritmo começa devagar. A velocidade só sobe depois de 1 min 30 s e, a partir daí, a cada 40 s.',
    gameStart: text.gameStart || 'Iniciar partida',
    gameOverTitle: text.gameOverTitle || 'Excelente percurso',
    gameOverText: text.gameOverText || 'Suas sobrevidas acabaram. Pontos:',
    gameAgain: text.gameAgain || 'Jogar novamente',
    gameJump: text.gameJump || 'Pular',
    gameHelp: text.gameHelp || 'Espaço, seta para cima ou toque',
    gameApple: text.gameApple || 'Maçã = 1 sobrevida',
    gameScore: text.gameScore || 'Pontos',
    gameRecord: text.gameRecord || 'Recorde',
    gameLives: text.gameLives || 'Sobrevidas',
    speedStart: 2.2,
    rampAfterSec: 90,
    rampEverySec: 40,
    speedStep: 0.35,
    speedMax: 5.6,
    showFooter: true,
    refreshLabel: text.refreshLabel || 'Atualizar status',
    incident: text.incident || 'Ref. #MNT-89240',
    protectedLabel: text.protectedLabel || 'Dados protegidos',
  };
}

export function maintenanceThemeColors(theme) {
  return theme === 'dark' ? { ...MAINTENANCE_DARK } : { ...MAINTENANCE_LIGHT };
}

function resolve(content) {
  const base = createDefaultMaintenanceContent();
  const merged = { ...base, ...(content || {}) };
  const preset = maintenanceThemeColors(merged.theme);
  const pick = (key) => safeColor(merged[key], preset[key]);
  return {
    ...merged,
    bgColor: pick('bgColor'),
    textColor: pick('textColor'),
    mutedColor: pick('mutedColor'),
    cardColor: pick('cardColor'),
    cardText: pick('cardText'),
    accentColor: pick('accentColor'),
    accent2Color: pick('accent2Color'),
    okColor: pick('okColor'),
    trackColor: pick('trackColor'),
    bgImage: safeUrl(merged.bgImage),
    publishMaintenance: merged.publishMaintenance === 'on' || merged.publishMaintenance === 'off' ? merged.publishMaintenance : 'keep',
    startsAt: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(String((content || {}).startsAt || '')) ? String((content || {}).startsAt).slice(0, 16) : '',
    endsAt: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(String((content || {}).endsAt || '')) ? String((content || {}).endsAt).slice(0, 16) : '',
    startsAtMs: maintenanceInstantFromInput((content || {}).startsAt),
    endsAtMs: maintenanceInstantFromInput((content || {}).endsAt),
    speedStart: clamp(merged.speedStart, 0.6, 8, 2.2),
    rampAfterSec: clamp(merged.rampAfterSec, 0, 600, 90),
    rampEverySec: clamp(merged.rampEverySec, 5, 600, 40),
    speedStep: clamp(merged.speedStep, 0.05, 3, 0.35),
    speedMax: Math.max(
      clamp(merged.speedMax, 1, 12, 5.6),
      clamp(merged.speedStart, 0.6, 8, 2.2)
    ),
    diagnostics: Array.isArray(merged.diagnostics) && merged.diagnostics.length
      ? merged.diagnostics.slice(0, 8).map((item) => ({
        name: String(item?.name || '').slice(0, 80),
        status: String(item?.status || '').slice(0, 40),
        tone: item?.tone === 'busy' || item?.tone === 'info' ? item.tone : 'ok',
      }))
      : base.diagnostics,
  };
}

const MAINTENANCE_CSS = `
.madri-mnt{--mnt-bg:#f8f9ff;--mnt-text:#0b1c30;--mnt-muted:#464555;--mnt-card:#fff;--mnt-card-text:#0b1c30;--mnt-accent:#4f46e5;--mnt-accent2:#0891b2;--mnt-ok:#059669;--mnt-track:#e5eeff;position:relative;box-sizing:border-box;width:100%;max-width:100%;min-height:640px;margin:0;padding:48px 20px;background:var(--mnt-bg);color:var(--mnt-text);font-family:Inter,Segoe UI,sans-serif;overflow:hidden}
.madri-moodle-layout.madri-layout-full .madri-row:has(.madri-mnt)>.madri-row-inner{max-width:none !important;width:100% !important;padding-left:0 !important;padding-right:0 !important}
.madri-moodle-layout.madri-layout-full .madri-row:has(.madri-mnt)>.madri-row-inner>[class*="madri-col-"]{padding-left:0 !important;padding-right:0 !important;max-width:100% !important;flex:1 1 100% !important}
.madri-mnt--fill{min-height:100vh;min-height:100dvh}
.madri-mnt--stage{min-height:760px;width:100%;max-width:none;margin:0}
.madri-mnt *{box-sizing:border-box}
.madri-mnt__glow{position:absolute;border-radius:999px;filter:blur(40px);pointer-events:none}
.madri-mnt__glow--a{top:-80px;left:50%;width:520px;height:280px;transform:translateX(-50%);background:var(--mnt-accent);opacity:.18}
.madri-mnt__glow--b{top:42%;left:18%;width:280px;height:280px;background:var(--mnt-accent2);opacity:.14}
.madri-mnt__photo{position:absolute;inset:0;background-size:cover;background-position:center;opacity:.28;pointer-events:none}
.madri-mnt__wrap{position:relative;z-index:1;width:min(920px,100%);margin:0 auto;display:flex;flex-direction:column;align-items:center}
.madri-mnt__badge{display:inline-flex;align-items:center;gap:8px;padding:6px 12px;border-radius:999px;background:var(--mnt-card);border:1px solid var(--mnt-track);font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--mnt-accent)}
.madri-mnt__dot{width:8px;height:8px;border-radius:999px;background:var(--mnt-accent);box-shadow:0 0 0 0 color-mix(in srgb,var(--mnt-accent) 55%,transparent);animation:madriMntPing 1.8s ease-out infinite}
@keyframes madriMntPing{70%{box-shadow:0 0 0 8px transparent}100%{box-shadow:0 0 0 0 transparent}}
.madri-mnt__ver{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:10px;padding:2px 6px;border-radius:6px;background:color-mix(in srgb,var(--mnt-text) 8%,transparent);color:var(--mnt-muted)}
.madri-mnt-title{margin:18px 0 8px;text-align:center;font-family:"Plus Jakarta Sans",Inter,sans-serif;font-size:40px;line-height:1.15;font-weight:700;letter-spacing:-.03em;color:var(--mnt-text)}
.madri-mnt-text{margin:0 auto 22px;max-width:640px;text-align:center;font-size:16px;line-height:1.6;color:var(--mnt-muted)}
.madri-mnt__grid{width:100%;display:grid;grid-template-columns:1.2fr .8fr;gap:16px}
.madri-mnt__grid--one{grid-template-columns:1fr;max-width:560px}
.madri-mnt__card{background:var(--mnt-card);color:var(--mnt-card-text);border:1px solid var(--mnt-track);border-radius:20px;padding:20px;box-shadow:0 16px 40px -18px rgba(15,23,42,.18)}
.madri-mnt__kicker{font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--mnt-muted)}
.madri-mnt__row{display:flex;justify-content:space-between;align-items:flex-end;gap:12px}
.madri-mnt__pct{font-family:"Plus Jakarta Sans",Inter,sans-serif;font-size:28px;font-weight:700;color:var(--mnt-accent);line-height:1}
.madri-mnt__hint{margin-top:4px;font-size:12px;color:var(--mnt-muted)}
.madri-mnt__track{margin-top:14px;height:12px;border-radius:999px;background:var(--mnt-track);overflow:hidden;padding:2px}
.madri-mnt__bar{height:100%;border-radius:999px;background:linear-gradient(90deg,var(--mnt-accent),var(--mnt-accent2))}
.madri-mnt__meta{display:flex;justify-content:space-between;gap:8px;margin-top:10px;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:11px;color:var(--mnt-muted)}
.madri-mnt__clock{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-top:14px}
.madri-mnt__tick{text-align:center;border-radius:12px;padding:10px 6px;background:var(--mnt-track)}
.madri-mnt__tick b{display:block;font-size:22px;font-family:"Plus Jakarta Sans",Inter,sans-serif}
.madri-mnt__tick span{font-size:10px;letter-spacing:.05em;text-transform:uppercase;color:var(--mnt-muted)}
.madri-mnt__diag{display:flex;flex-direction:column;gap:8px;margin-top:12px}
.madri-mnt__line{display:flex;justify-content:space-between;align-items:center;gap:8px;padding:8px 10px;border-radius:12px;background:var(--mnt-track);font-size:14px}
.madri-mnt__pill{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:10px;padding:3px 8px;border-radius:999px;background:var(--mnt-card)}
.madri-mnt__pill--ok{color:var(--mnt-ok)}
.madri-mnt__pill--busy{color:var(--mnt-accent2)}
.madri-mnt__pill--info{color:var(--mnt-accent)}
.madri-mnt__footmeta{display:flex;justify-content:space-between;margin-top:12px;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:10px;letter-spacing:.04em;text-transform:uppercase;color:var(--mnt-muted)}
.madri-mnt__game{width:100%;margin-top:16px}
.madri-mnt__gamehead{display:flex;justify-content:space-between;gap:12px;align-items:flex-start;margin-bottom:12px}
.madri-mnt__gamehead h2{margin:0;font-size:18px;font-family:"Plus Jakarta Sans",Inter,sans-serif}
.madri-mnt__chip{display:inline-flex;margin-left:8px;padding:2px 8px;border-radius:999px;background:var(--mnt-track);color:var(--mnt-accent);font-size:11px;font-weight:700}
.madri-mnt__stage{position:relative;height:240px;border-radius:14px;overflow:hidden;border:1px solid var(--mnt-track);background:var(--mnt-track)}
.madri-mnt__stage canvas{width:100%;height:100%;display:block}
.madri-mnt__hud{position:absolute;top:10px;left:10px;right:10px;display:flex;justify-content:space-between;gap:8px;pointer-events:none;font-size:12px}
.madri-mnt__hud span{background:var(--mnt-card);border-radius:10px;padding:6px 8px}
.madri-mnt__overlay{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:16px;background:var(--mnt-bg)}
.madri-mnt__overlay p{margin:6px 0 12px;max-width:420px;color:var(--mnt-muted);font-size:13px}
.madri-mnt__btn{border:0;border-radius:10px;padding:8px 14px;background:var(--mnt-accent);color:#fff;font-weight:700;cursor:pointer}
.madri-mnt__controls{display:flex;justify-content:space-between;gap:8px;align-items:center;margin-top:10px;font-size:12px;color:var(--mnt-muted)}
.madri-mnt__footer{display:flex;flex-wrap:wrap;justify-content:center;gap:8px 14px;margin-top:18px;font-size:12px;color:var(--mnt-muted)}
.madri-mnt__footer button{background:transparent;border:0;color:inherit;cursor:pointer;font:inherit}
.madri-mnt-hidden{display:none !important}
@media (max-width:767px){
  .madri-mnt{padding:28px 14px}
  .madri-mnt-title{font-size:28px}
  .madri-mnt__grid,.madri-mnt__grid--one{grid-template-columns:1fr}
  .madri-mnt__gamehead{flex-direction:column}
}
`;

function runtimeScript(id, cfg) {
  const data = JSON.stringify({
    start: cfg.startsAtMs,
    end: cfg.endsAtMs,
    speed: cfg.speedStart,
    rampAfter: cfg.rampAfterSec,
    rampEvery: cfg.rampEverySec,
    step: cfg.speedStep,
    max: cfg.speedMax,
    accent: cfg.accentColor,
    accent2: cfg.accent2Color,
    ok: cfg.okColor,
    track: cfg.trackColor,
    text: cfg.cardText,
    muted: cfg.mutedColor,
  });
  return `<script>
(function(){
  var root=document.getElementById(${JSON.stringify(id)});
  if(!root||root.getAttribute('data-mnt-booted')==='1') return;
  root.setAttribute('data-mnt-booted','1');
  if(window.self===window.top) root.classList.add('madri-mnt--fill');
  var cfg=${data};
  var hours=root.querySelector('[data-mnt-h]');
  var mins=root.querySelector('[data-mnt-m]');
  var secs=root.querySelector('[data-mnt-s]');
  var pct=root.querySelector('[data-mnt-pct]');
  var bar=root.querySelector('[data-mnt-bar]');
  var origin=Date.now();
  var server=origin;
  if(typeof window.geppettoServerNow==='number' && window.geppettoServerNow>1000000000){
    server=window.geppettoServerNow*1000;
  }
  function nowMs(){ return server+(Date.now()-origin); }
  function paintClock(){
    var now=nowMs();
    var begin=cfg.start, end=cfg.end, left=0, percent=0;
    if(end>begin && begin>0){
      percent=Math.round(Math.max(0, Math.min(100, ((now-begin)/(end-begin))*100)));
    } else if(end>0 && now>=end){
      percent=100;
    }
    if(end>now) left=Math.floor((end-now)/1000);
    if(hours) hours.textContent=String(Math.floor(left/3600)).padStart(2,'0');
    if(mins) mins.textContent=String(Math.floor((left%3600)/60)).padStart(2,'0');
    if(secs) secs.textContent=String(left%60).padStart(2,'0');
    if(pct) pct.textContent=percent+'%';
    if(bar) bar.style.width=percent+'%';
  }
  if(cfg.end>0){
    paintClock();
    setInterval(paintClock,1000);
  }
  var canvas=root.querySelector('canvas');
  if(!canvas) return;
  var ctx=canvas.getContext('2d');
  var cover=root.querySelector('[data-mnt-start]');
  var over=root.querySelector('[data-mnt-over]');
  var scoreEl=root.querySelector('[data-mnt-score]');
  var bestEl=root.querySelector('[data-mnt-best]');
  var livesEl=root.querySelector('[data-mnt-lives]');
  var finalEl=root.querySelector('[data-mnt-final]');
  var state='idle', frame=0, score=0, lives=1, speed=cfg.speed, raf=0, inv=0;
  var best=0;
  try{ best=Number(localStorage.getItem('geppetto-salto-recorde'))||0; }catch(e){}
  if(bestEl) bestEl.textContent=String(best);
  var ground=188, player={x:70,y:ground-32,w:28,h:32,vy:0,g:0.5,jump:-11.2,cut:-4,minRise:100,maxRise:145,on:true};
  var obstacles=[], books=[], apples=[], bits=[];
  var clouds=[{x:140,y:34,s:1},{x:430,y:56,s:0.82},{x:760,y:26,s:1.12}];
  var obstacleLabels=['Dúvida','Distração','Procrastinação','Insegurança'];
  var obstacleColors=['#ba1a1a','#e11d48','#d97706'];
  var spawnIn=460, scroll=0, released=true, audioCtx=null;
  function roundBox(x,y,w,h,r){
    ctx.beginPath();
    if(ctx.roundRect){ ctx.roundRect(x,y,w,h,r); }
    else { ctx.rect(x,y,w,h); }
  }
  function paint(){
    if(scoreEl) scoreEl.textContent=String(Math.floor(score));
    if(livesEl) livesEl.textContent=String(lives);
    if(bestEl) bestEl.textContent=String(Math.floor(best));
  }
  function burst(x,y,color){
    for(var i=0;i<8;i++) bits.push({x:x,y:y,vx:(Math.random()-0.5)*4,vy:(Math.random()-0.8)*4,a:1,c:color});
  }
  function reset(){
    score=0; lives=1; speed=cfg.speed; frame=0; inv=0; spawnIn=460; scroll=0; released=true;
    player.y=ground-player.h; player.vy=0; player.on=true;
    obstacles=[]; books=[]; apples=[]; bits=[];
    clouds=[{x:140,y:34,s:1},{x:430,y:56,s:0.82},{x:760,y:26,s:1.12}];
    paint();
  }
  function blip(freq, dur, delay){
    try{
      var Ctx=window.AudioContext||window.webkitAudioContext;
      if(!Ctx) return;
      if(!audioCtx) audioCtx=new Ctx();
      if(audioCtx.state==='suspended') audioCtx.resume();
      var t=audioCtx.currentTime+(delay||0);
      var osc=audioCtx.createOscillator();
      var gain=audioCtx.createGain();
      osc.type='square';
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.04, t+0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, t+dur);
      osc.connect(gain); gain.connect(audioCtx.destination);
      osc.start(t); osc.stop(t+dur+0.02);
    }catch(err){}
  }
  function jump(){
    if(state!=='run'){ begin(); return; }
    if(player.on){
      player.vy=player.jump-(speed/16);
      player.on=false;
      released=false;
      blip(640, 0.07, 0);
    }
  }
  function release(){ released=true; }
  function begin(){
    reset();
    state='run';
    if(cover) cover.classList.add('madri-mnt-hidden');
    if(over) over.classList.add('madri-mnt-hidden');
    cancelAnimationFrame(raf);
    loop();
  }
  function fail(){
    state='over';
    if(score>best){ best=Math.floor(score); try{ localStorage.setItem('geppetto-salto-recorde', String(best)); }catch(e){} }
    if(finalEl) finalEl.textContent=String(Math.floor(score));
    if(over) over.classList.remove('madri-mnt-hidden');
    paint();
  }
  function hit(a,b){
    var by=b.y+(b.float||0);
    var top=b.kind==='obs'?4:2;
    var side=b.kind==='obs'?3:1;
    return a.x+4<b.x+b.w-side && a.x+a.w-4>b.x+side && a.y+5<by+b.h && a.y+a.h-2>by+top;
  }
  function loop(){
    if(state!=='run') return;
    frame++;
    score+=0.05;
    if(score>best) best=score;
    var after=Math.round(cfg.rampAfter*60);
    var every=Math.max(30, Math.round(cfg.rampEvery*60));
    if(frame>after && (frame-after)%every===0 && speed<cfg.max){
      speed=Math.min(cfg.max, speed+cfg.step);
      blip(520, 0.08, 0); blip(780, 0.11, 0.09);
    }
    if(inv>0) inv--;
    player.y+=player.vy; player.vy+=player.g;
    var risen=ground-player.h-player.y;
    if(player.vy<player.cut && (released && risen>=player.minRise || risen>=player.maxRise)) player.vy=player.cut;
    if(player.y>=ground-player.h){ player.y=ground-player.h; player.vy=0; player.on=true; }
    spawnIn-=speed;
    if(spawnIn<=0){
      var large=Math.random()<0.25;
      var oh=large?34:22;
      var ow=large?16:12;
      obstacles.push({x:canvas.width+8,y:ground-oh,w:ow,h:oh,kind:'obs',text:obstacleLabels[Math.floor(Math.random()*obstacleLabels.length)],color:obstacleColors[Math.floor(Math.random()*obstacleColors.length)]});
      spawnIn=260+Math.random()*180+speed*36;
    }
    if(frame%170===0) books.push({x:canvas.width+20,y:ground-42-Math.random()*8,w:20,h:20,float:0});
    if(frame%700===0) apples.push({x:canvas.width+20,y:ground-44-Math.random()*10,w:22,h:22,float:0});
    var i;
    for(i=obstacles.length-1;i>=0;i--){
      obstacles[i].x-=speed;
      if(inv===0 && hit(player, obstacles[i])){
        if(lives>1){ lives--; inv=80; obstacles.splice(i,1); burst(player.x, player.y, cfg.accent); continue; }
        lives=0; fail(); return;
      }
      if(obstacles[i].x<-40) obstacles.splice(i,1);
    }
    for(i=books.length-1;i>=0;i--){
      books[i].x-=speed;
      books[i].float=Math.sin(frame*0.1)*3;
      if(hit(player, books[i])){ score+=12; burst(books[i].x, books[i].y, '#4f46e5'); books.splice(i,1); continue; }
      if(books[i].x<-40) books.splice(i,1);
    }
    for(i=apples.length-1;i>=0;i--){
      apples[i].x-=speed;
      apples[i].float=Math.sin(frame*0.1)*4;
      if(hit(player, apples[i])){ lives++; score+=20; burst(apples[i].x, apples[i].y, '#ba1a1a'); apples.splice(i,1); continue; }
      if(apples[i].x<-40) apples.splice(i,1);
    }
    for(i=bits.length-1;i>=0;i--){ bits[i].x+=bits[i].vx; bits[i].y+=bits[i].vy; bits[i].a-=0.04; if(bits[i].a<=0) bits.splice(i,1); }
    ctx.clearRect(0,0,canvas.width,canvas.height);
    scroll+=speed;
    var ci;
    for(ci=0;ci<clouds.length;ci++){
      clouds[ci].x-=speed*0.18;
      if(clouds[ci].x<-80) clouds[ci].x=canvas.width+30+Math.random()*220;
      var cl=clouds[ci], cw=46*cl.s;
      ctx.fillStyle='rgba(255,255,255,0.78)';
      ctx.beginPath();
      ctx.ellipse(cl.x, cl.y, cw*0.42, 8*cl.s, 0, 0, 6.28);
      ctx.ellipse(cl.x+cw*0.28, cl.y+2, cw*0.28, 6*cl.s, 0, 0, 6.28);
      ctx.ellipse(cl.x-cw*0.24, cl.y+3, cw*0.24, 5.5*cl.s, 0, 0, 6.28);
      ctx.fill();
    }
    ctx.beginPath();
    ctx.moveTo(0, ground+6-Math.sin(scroll*0.065)*4.2-Math.sin(scroll*0.021)*2.1);
    var gx;
    for(gx=6;gx<=canvas.width;gx+=6){
      var gu=gx+scroll;
      ctx.lineTo(gx, ground+6-Math.sin(gu*0.065)*4.2-Math.sin(gu*0.021)*2.1);
    }
    ctx.strokeStyle=cfg.text; ctx.globalAlpha=0.7; ctx.lineWidth=2; ctx.lineJoin='round'; ctx.stroke(); ctx.globalAlpha=1;
    function drawBook(b){
      var cy=b.y+(b.float||0);
      ctx.fillStyle='#4f46e5'; roundBox(b.x,cy,b.w,b.h,3); ctx.fill();
      ctx.fillStyle='#67f4b7'; ctx.fillRect(b.x+4,cy,3,7);
      ctx.fillStyle='#ffffff'; ctx.fillRect(b.x+b.w-3,cy+2,2,b.h-4);
    }
    function drawApple(a){
      var ay=a.y+(a.float||0);
      ctx.save(); ctx.shadowColor='rgba(239,68,68,0.4)'; ctx.shadowBlur=8; ctx.fillStyle='#ba1a1a';
      ctx.beginPath(); ctx.arc(a.x+8,ay+11,7.5,0,6.28); ctx.arc(a.x+14,ay+11,7.5,0,6.28); ctx.fill(); ctx.restore();
      ctx.fillStyle='#15803d'; ctx.beginPath(); ctx.ellipse(a.x+15,ay+3,4,2,0.8,0,6.28); ctx.fill();
      ctx.strokeStyle='#78350f'; ctx.lineWidth=1.5; ctx.beginPath(); ctx.moveTo(a.x+11,ay+5); ctx.lineTo(a.x+11,ay+2); ctx.stroke();
      ctx.font='bold 9px ui-monospace,monospace'; ctx.fillStyle='#ba1a1a'; ctx.textAlign='center'; ctx.fillText('+1 Vida', a.x+11, ay-4);
    }
    function drawObstacle(o){
      ctx.fillStyle=o.color||'#e11d48'; roundBox(o.x,o.y,o.w,o.h,5); ctx.fill();
      ctx.fillStyle='#ffffff';
      ctx.fillRect(o.x+o.w/2-1, o.y+4, 2, o.h-10);
      ctx.fillRect(o.x+o.w/2-1, o.y+o.h-4, 2, 2);
      ctx.font='10px Inter,sans-serif'; ctx.fillStyle=cfg.muted; ctx.textAlign='center';
      ctx.fillText(o.text||'', o.x+o.w/2, o.y-5);
    }
    function drawPenguin(){
      if(inv%6>=3) return;
      ctx.fillStyle='#3525cd'; roundBox(player.x, player.y, player.w, player.h, [9,9,5,5]); ctx.fill();
      ctx.fillStyle='#e2dfff'; ctx.beginPath(); ctx.ellipse(player.x+player.w/2, player.y+player.h-11, 8, 9, 0, 0, 6.28); ctx.fill();
      ctx.fillStyle='#ffffff'; ctx.beginPath(); ctx.arc(player.x+9, player.y+10, 4, 0, 6.28); ctx.arc(player.x+19, player.y+10, 4, 0, 6.28); ctx.fill();
      ctx.fillStyle='#0b1c30'; ctx.beginPath(); ctx.arc(player.x+10.5, player.y+10, 1.8, 0, 6.28); ctx.arc(player.x+20.5, player.y+10, 1.8, 0, 6.28); ctx.fill();
      ctx.fillStyle='#f59e0b'; ctx.beginPath(); ctx.moveTo(player.x+13, player.y+13); ctx.lineTo(player.x+16, player.y+13); ctx.lineTo(player.x+14.5, player.y+17); ctx.closePath(); ctx.fill();
      ctx.fillStyle='#0b1c30'; ctx.beginPath();
      ctx.moveTo(player.x+14, player.y-6); ctx.lineTo(player.x+26, player.y-2); ctx.lineTo(player.x+14, player.y+2); ctx.lineTo(player.x+2, player.y-2); ctx.closePath(); ctx.fill();
      ctx.strokeStyle='#57dffe'; ctx.lineWidth=1.5; ctx.beginPath(); ctx.moveTo(player.x+14, player.y-2); ctx.lineTo(player.x+24, player.y+4); ctx.stroke();
      if(player.on){
        var swing=Math.sin(frame*0.35)*3;
        ctx.fillStyle='#f59e0b';
        ctx.fillRect(player.x+6, ground-2, 4, 2+swing);
        ctx.fillRect(player.x+18, ground-2, 4, 2-swing);
      }
      if(lives>1){
        ctx.save(); ctx.strokeStyle='rgba(239,68,68,0.45)'; ctx.lineWidth=1.5; ctx.setLineDash([4,4]);
        ctx.beginPath(); ctx.arc(player.x+player.w/2, player.y+player.h/2, player.h*0.75, 0, 6.28); ctx.stroke(); ctx.restore();
      }
    }
    books.forEach(drawBook);
    apples.forEach(drawApple);
    obstacles.forEach(drawObstacle);
    bits.forEach(function(p){ ctx.globalAlpha=Math.max(p.a,0); ctx.fillStyle=p.c; ctx.beginPath(); ctx.arc(p.x,p.y,2.5,0,6.28); ctx.fill(); ctx.globalAlpha=1; });
    drawPenguin();
    paint();
    raf=requestAnimationFrame(loop);
  }
  root.querySelectorAll('[data-mnt-jump]').forEach(function(btn){
    btn.addEventListener('pointerdown', function(ev){ ev.preventDefault(); jump(); });
    btn.addEventListener('pointerup', release);
    btn.addEventListener('pointerleave', release);
  });
  window.addEventListener('keydown', function(ev){
    if(!root.contains(document.activeElement) && ev.target && (ev.target.tagName==='INPUT' || ev.target.tagName==='TEXTAREA')) return;
    if(ev.code==='Space' || ev.code==='ArrowUp'){ if(ev.repeat) return; ev.preventDefault(); jump(); }
  });
  window.addEventListener('keyup', function(ev){
    if(ev.code==='Space' || ev.code==='ArrowUp') release();
  });
  paint();
})();
</script>`;
}

export function buildMaintenancePageInnerHtml(content, widgetId, options = {}) {
  const cfg = resolve(content);
  const stage = !!(options && options.stage);
  const id = `madri-mnt-${safeId(widgetId)}`;
  const clock = clockFromWindow(cfg.startsAtMs, cfg.endsAtMs, Date.now());
  const forecastText = formatSchedule(cfg.endsAt);
  const photo = cfg.bgImage
    ? `<div class="madri-mnt__photo" style="background-image:url('${escAttr(cfg.bgImage)}')"></div>`
    : '';
  const progress = (cfg.showProgress || cfg.showCountdown) ? `
    <section class="madri-mnt__card">
      ${cfg.showProgress ? `<div class="madri-mnt__row">
        <div>
          <div class="madri-mnt__kicker">${escText(cfg.progressLabel)}</div>
          <div class="madri-mnt__hint">${escText(cfg.progressHint)}</div>
        </div>
        <div class="madri-mnt__pct" data-mnt-pct>${clock.percent}%</div>
      </div>
      <div class="madri-mnt__track"><div class="madri-mnt__bar" data-mnt-bar style="width:${clock.percent}%"></div></div>` : ''}
      ${forecastText ? `<div class="madri-mnt__meta"><span>${escText(cfg.forecastLabel)}: ${escText(forecastText)}</span></div>` : ''}
      ${cfg.showCountdown ? `<div class="madri-mnt__clock">
        <div class="madri-mnt__tick"><b data-mnt-h>${pad2(clock.hours)}</b><span>${escText(cfg.hoursLabel)}</span></div>
        <div class="madri-mnt__tick"><b data-mnt-m style="color:var(--mnt-accent)">${pad2(clock.minutes)}</b><span>${escText(cfg.minutesLabel)}</span></div>
        <div class="madri-mnt__tick"><b data-mnt-s>${pad2(clock.seconds)}</b><span>${escText(cfg.secondsLabel)}</span></div>
      </div>` : ''}
    </section>` : '';
  const lines = cfg.diagnostics.map((item) => `
    <div class="madri-mnt__line">
      <span>${escText(item.name)}</span>
      <span class="madri-mnt__pill madri-mnt__pill--${item.tone}">${escText(item.status)}</span>
    </div>`).join('');
  const diagnostics = cfg.showDiagnostics ? `
    <section class="madri-mnt__card">
      <div class="madri-mnt__row"><span class="madri-mnt__kicker">${escText(cfg.diagnosticsTitle)}</span><span class="madri-mnt__dot"></span></div>
      <div class="madri-mnt__diag">${lines}</div>
      <div class="madri-mnt__footmeta"><span>${escText(cfg.latency)}</span><span>${escText(cfg.cluster)}</span></div>
    </section>` : '';
  const both = (cfg.showProgress || cfg.showCountdown) && cfg.showDiagnostics;
  const grid = (progress || diagnostics)
    ? `<div class="madri-mnt__grid${both ? '' : ' madri-mnt__grid--one'}">${progress}${diagnostics}</div>`
    : '';
  const game = cfg.showGame ? `
    <section class="madri-mnt__card madri-mnt__game">
      <div class="madri-mnt__gamehead">
        <div>
          <h2 class="madri-mnt-title" style="font-size:20px;margin:0;text-align:left">${escText(cfg.gameTitle)}<span class="madri-mnt__chip">${escText(cfg.gameBadge)}</span></h2>
          <p class="madri-mnt-text" style="text-align:left;margin:6px 0 0">${escText(cfg.gameText)}</p>
        </div>
      </div>
      <div class="madri-mnt__stage">
        <canvas width="1000" height="240"></canvas>
        <div class="madri-mnt__hud"><span>${escText(cfg.gameLives)} <b data-mnt-lives>1</b></span><span>${escText(cfg.gameScore)} <b data-mnt-score>0</b> · ${escText(cfg.gameRecord)} <b data-mnt-best>0</b></span></div>
        <div class="madri-mnt__overlay" data-mnt-start>
          <strong>${escText(cfg.gameTitle)}</strong>
          <p class="madri-mnt-text">${escText(cfg.gameText)}</p>
          <button type="button" class="madri-mnt__btn" data-mnt-jump>${escText(cfg.gameStart)}</button>
        </div>
        <div class="madri-mnt__overlay madri-mnt-hidden" data-mnt-over>
          <strong>${escText(cfg.gameOverTitle)}</strong>
          <p class="madri-mnt-text">${escText(cfg.gameOverText)} <b data-mnt-final>0</b></p>
          <button type="button" class="madri-mnt__btn" data-mnt-jump>${escText(cfg.gameAgain)}</button>
        </div>
      </div>
      <div class="madri-mnt__controls">
        <span>${escText(cfg.gameHelp)} · ${escText(cfg.gameApple)}</span>
        <button type="button" class="madri-mnt__btn" data-mnt-jump>${escText(cfg.gameJump)}</button>
      </div>
    </section>` : '';
  const footer = cfg.showFooter ? `
    <div class="madri-mnt__footer">
      <button type="button" onclick="location.reload()">${escText(cfg.refreshLabel)}</button>
      <span>${escText(cfg.incident)}</span>
      <span>${escText(cfg.protectedLabel)}</span>
    </div>` : '';
  const styleVars = [
    `--mnt-bg:${cfg.bgColor}`,
    `--mnt-text:${cfg.textColor}`,
    `--mnt-muted:${cfg.mutedColor}`,
    `--mnt-card:${cfg.cardColor}`,
    `--mnt-card-text:${cfg.cardText}`,
    `--mnt-accent:${cfg.accentColor}`,
    `--mnt-accent2:${cfg.accent2Color}`,
    `--mnt-ok:${cfg.okColor}`,
    `--mnt-track:${cfg.trackColor}`,
  ].join(';');
  return `<style>${MAINTENANCE_CSS}</style>
<section id="${id}" class="madri-mnt${stage ? ' madri-mnt--stage' : ''}" style="${styleVars}" data-gp-maintenance="1">
  ${photo}
  <div class="madri-mnt__glow madri-mnt__glow--a" style="background:${hexToRgba(cfg.accentColor, 0.22)}"></div>
  <div class="madri-mnt__glow madri-mnt__glow--b" style="background:${hexToRgba(cfg.accent2Color, 0.16)}"></div>
  <div class="madri-mnt__wrap">
    <div class="madri-mnt__badge"><span class="madri-mnt__dot"></span>${escText(cfg.badge)} <span class="madri-mnt__ver">${escText(cfg.version)}</span></div>
    <h1 class="madri-mnt-title">${escText(cfg.title)}</h1>
    <p class="madri-mnt-text">${escText(cfg.subtitle)}</p>
    ${grid}
    ${game}
    ${footer}
  </div>
  ${stage ? '' : runtimeScript(id, cfg)}
</section>`;
}

export function collectMaintenanceIntent(rows) {
  let intent = 'keep';
  const visit = (list) => {
    (list || []).forEach((row) => {
      (row.columns || []).forEach((col) => {
        (col.widgets || []).forEach((widget) => {
          if (widget && widget.type === 'site-maintenance') {
            const flag = widget.content && widget.content.publishMaintenance;
            if (flag === 'on' || flag === 'off') intent = flag;
          }
        });
        if (col.rows) visit(col.rows);
        if (col.nestedRows) visit(col.nestedRows);
      });
    });
  };
  visit(rows);
  return intent;
}
